import { Injectable } from '@angular/core';
import { createSelector, select, Store } from '@ngrx/store';
import {
    EditedPhotoData,
    Photo,
    SelectedPhotoData
} from '@classifieds-sell/trademe-core-ui';
import {
    SaveDraft,
    ValidationResult,
    UpdateCurrentStep,
    CategoryEnum,
    ListingRequest
} from 'libs/datalayer/src';
import * as DraftActions from './motors.draft.actions';
import * as DraftFeature from './motors.draft.reducer';
import * as DraftSelectors from './motors.draft.selectors';
import { every, filter, map, sample, takeUntil } from 'rxjs/operators';
import { MotorsDraftState } from './motors.draft.reducer';
import { getDraftStateMotors } from './motors.draft.selectors';

@Injectable()
export class MotorsDraftFacade {
    state$ = this.store.pipe(select(DraftSelectors.getStateMotors));
    draftLoaded$ = this.store.pipe(select(DraftSelectors.getDraftLoadedMotors));
    draftData$ = this.store.pipe(select(DraftSelectors.getDraftDataMotors));
    validation$ = this.store.pipe(select(DraftSelectors.getValidationSchemaMotors));
    validationResults$ = this.store.pipe(
        select(DraftSelectors.getValidationResultsMotors)
    );
    validateListingResponse$ = this.store.pipe(
        select(DraftSelectors.validateListingResponseMotors)
    );
    listingActionLoading$ = this.store.pipe(
        select(DraftSelectors.getListingActionLoadingMotors)
    );
    listingActionResponse$ = this.store.pipe(
        select(DraftSelectors.getListingActionResponseMotors)
    );

    photosUploading$ = this.draftData$.pipe(
        filter(draft => !!draft),
        map(draft => draft.descriptionAndPhotos?.photos as Photo[] || []),
        map(photos => photos.some(p => p.loading)));

    constructor(private store: Store<DraftFeature.MotorsDraftPartialState>) { }

    init(categoryId: CategoryEnum, listingId?: number) {
        this.store.dispatch(
            DraftActions.getDraft({ categoryId, listingId })
        );
    }

    updateDraftData(draftData: any) {
        this.store.dispatch(DraftActions.updateDraftData({ draftData }));
    }

    updateValidationResults(validationResult: ValidationResult) {
        this.store.dispatch(
            DraftActions.updateValidationResult({ validationResult })
        );
    }

    saveDraft(draft: SaveDraft) {
        this.store.dispatch(DraftActions.saveDraft({ draft }));
    }

    resetLoadingStatus() {
        this.store.dispatch(DraftActions.resetSaveDraftLoadingStatus());
    }

    makeListingAction(listingRequest: ListingRequest) {
        this.store.dispatch(DraftActions.makeListingAction({ listingRequest }));
    }

    validateListing() {
        this.store.dispatch(DraftActions.validateListing());
    }

    addPhotoToDraftData(
        photo: Photo,
        selectedPhotoData: SelectedPhotoData,
        parentProperty: string
    ) {
        this.store.dispatch(
            DraftActions.addPhotoToDraftData({
                photo,
                selectedPhotoData,
                parentProperty
            })
        );
    }

    updatePhotosInDraftData(photos: Photo[], parentProperty: string) {
        this.store.dispatch(
            DraftActions.updatePhotosInDraftData({ photos, parentProperty })
        );
    }

    updateTransformPhotoDataToDraftData(
        editedPhotoData: EditedPhotoData,
        parentProperty: string
    ) {
        this.store.dispatch(
            DraftActions.updateTransformPhotoDataToDraftData({
                editedPhotoData,
                parentProperty
            })
        );
    }

    updateCurrentStep(currentStep: UpdateCurrentStep) {
        this.store.dispatch(DraftActions.updateCurrentStep({ currentStep }));
    }

    resetPackageCodeIfNeeded({ isRelist }: { isRelist: boolean}) {
        this.store.dispatch(DraftActions.resetPackageCodeIfNeeded({ isRelist}));
    }
}
